import { useCreateReconnectionWithPayment } from "@features/cx/reconnect/hooks";
import { StepHelpers } from "@shared/components/Stepper";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { format } from "date-fns";
import {
  Calendar,
  CheckCircle,
  ChevronLeft,
  CreditCard as PaymentIcon,
  DollarSign,
  FileText,
  Globe,
  Loader2,
  Mail,
  Phone,
  Send,
  ShieldCheck,
  User,
  Users,
  MapPin,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField } from "@shared/services/FormServices";
import { useForm } from "react-hook-form";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { usePaymentMethod } from "@features/cx/payments/hooks";
import { ValueLabel } from "@features/ax/shared/ValueLabel";

interface ReviewPaymentStepProps {
  remoteUtilityId: number;
  storageKey: string;
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
}

interface ReviewData {
  customer?: {
    name: string;
    accountNumber: string;
    type: string;
    email: string;
    phone: string;
    address: string;
    id: number;
  };
  request?: {
    requestType: string;
    utilitySupportRequest: string;
    priority: string;
    priorityLevelDisplay?: string;
    scheduledDate: string | null;
    reason: string;
    timeSlot: string;
    reasonLabel: string;
    timeSlotLabel: string;
    consumerRemark: string;
    reconnectionFee: number;
    feesBreakdown: any[];
    accountStructurePreference: string;
    selectedMaster?: any;
    selectedOwner?: any;
    serviceAreaPreference: string;
    newHouseNumber?: string;
    newZipCode?: string;
    newCity?: string;
    newStreetAddress?: string;
    mailingAddressPreference: string;
    mailingHouseNumber?: string;
    mailingZipCode?: string;
    mailingCity?: string;
    mailingStreetAddress?: string;
    ownershipDisplay?: string;
    currentOwnerInfo?: any;
    newOwnerName?: string;
    newOwnerEmail?: string;
    newOwnerPhone?: string;
    newOwnerAddress?: string;
  };
}

const formatAddressDetail = (
  unit?: string,
  street?: string,
  city?: string,
  zip?: string,
) => {
  return [unit ? `${unit}` : "", street, city, zip].filter(Boolean).join(", ");
};

export function ReviewPaymentStep({
  remoteUtilityId,
  storageKey,
  stepHelpers,
  currentStepIndex = 2,
  onNext,
  onPrevious,
  setValidationError,
  clearValidationError,
}: ReviewPaymentStepProps) {
  const [reviewData, setReviewData] = useState<ReviewData>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dataLoadedRef = useRef(false);
  const { mutate: createReconnectRequest } = useCreateReconnectionWithPayment();

  console.log("reviewData", reviewData);

  // Helper function to save step data using stepHelpers
  const saveStepData = useCallback(
    (data: any) => {
      if (!stepHelpers) return;

      try {
        const stepData = {
          ...data,
          timestamp: new Date().toISOString(),
          stepIndex: currentStepIndex,
        };

        stepHelpers.setStepData(currentStepIndex, stepData);
        console.log("Review step data saved:", stepData);
      } catch (error) {
        console.error("Review step data save error:", error);
      }
    },
    [stepHelpers, currentStepIndex],
  );

  // Helper function to get step data using stepHelpers
  const getStepData = useCallback(
    (stepIndex: number) => {
      if (!stepHelpers) return null;

      try {
        return stepHelpers.getStepData(stepIndex);
      } catch (error) {
        console.error(`Failed to get step data for step ${stepIndex}:`, error);
        return null;
      }
    },
    [stepHelpers],
  );

  // Helper function to get all steps data
  const getAllStepsData = useCallback(() => {
    const customerData = getStepData(0); // Customer info step
    const requestData = getStepData(1); // Request details step
    const notificationData = getStepData(2); // Current step or notification step if exists

    return { customerData, requestData, notificationData };
  }, [getStepData]);

  console.log("getAllStepsData", getAllStepsData());

  // Load data from all previous steps
  useEffect(() => {
    if (stepHelpers && !dataLoadedRef.current) {
      try {
        const { customerData, requestData, notificationData } =
          getAllStepsData();

        console.log("Loading review data from all steps:", {
          customerData,
          requestData,
          notificationData,
        });

        const compiledReviewData: ReviewData = {};

        // Load customer data
        if (customerData) {
          compiledReviewData.customer = {
            id: customerData.selectedConsumerId,
            name: customerData.customerName || "",
            accountNumber: customerData.accountNumber || "",
            type:
              customerData.consumerMetadata?.categoryDisplay ||
              customerData.selectedCustomer?.customerType ||
              "Standard",
            email: customerData.contactEmail || "",
            phone: customerData.contactPhone || "",
            address: customerData.serviceAddress || "",
          };
        }

        // Load request details data
        if (requestData) {
          compiledReviewData.request = {
            requestType: "Reconnection",
            utilitySupportRequest: requestData.reason || "",
            reason: requestData.reason || "",
            reasonLabel: requestData.reasonLabel || "",
            timeSlotLabel: requestData.timeSlotLabel || "",
            priority: requestData.priority || "medium",
            priorityLevelDisplay: requestData.priorityLevelDisplay || "",
            scheduledDate: requestData.scheduledDate || null,
            timeSlot: requestData.timeSlot || "",
            consumerRemark: requestData.consumerRemark || "",
            reconnectionFee: Number(requestData.reconnectionFee) || 0,
            feesBreakdown: requestData.feesBreakdown || [],
            accountStructurePreference:
              requestData.accountStructurePreference || "keep_same_master",
            selectedMaster: requestData.selectedMaster || null,
            selectedOwner: requestData.selectedOwner || null,
            serviceAreaPreference: requestData.serviceAreaPreference || "same",
            newHouseNumber: requestData.newHouseNumber || "",
            newZipCode: requestData.newZipCode || "",
            newCity: requestData.newCity || "",
            newStreetAddress: requestData.newStreetAddress || "",
            mailingAddressPreference:
              requestData.mailingAddressPreference || "same_as_service",
            mailingHouseNumber: requestData.mailingHouseNumber || "",
            mailingZipCode: requestData.mailingZipCode || "",
            mailingCity: requestData.mailingCity || "",
            mailingStreetAddress: requestData.mailingStreetAddress || "",
            ownershipDisplay: requestData.ownershipDisplay || "Individual",
            currentOwnerInfo: requestData.currentOwnerInfo || null,
            newOwnerName: requestData.newOwnerName || "",
            newOwnerEmail: requestData.newOwnerEmail || "",
            newOwnerPhone: requestData.newOwnerPhone || "",
            newOwnerAddress: requestData.newOwnerAddress || "",
          };
        }

        setReviewData(compiledReviewData);
        dataLoadedRef.current = true;
      } catch (error) {
        console.error("Failed to load review data:", error);
        dataLoadedRef.current = true;
      }
    }
  }, [stepHelpers, currentStepIndex, getAllStepsData]);

  const currency = useCurrency();
  const { data: paymentMethodsData } = usePaymentMethod({
    remoteUtilityId,
  });

  const activePaymentMethods = useMemo(
    () => paymentMethodsData?.result?.filter((m: any) => m.isActive) || [],
    [paymentMethodsData],
  );

  const GRAND_TOTAL = reviewData.request?.reconnectionFee || 0;
  const paymentForm = useForm({
    defaultValues: {
      payment_method: "",
      amount: GRAND_TOTAL,
      cashReceived: "",
      paymentDate: "",
      chequeNo: "",
      bankName: "",
      accountHolderName: "",
      routingNumber: "",
      bankAccountNumber: "",
      chequeDate: "",
      chequeAmount: GRAND_TOTAL,
      onlineAmount: GRAND_TOTAL,
      referenceNumber: "",
      onlineDate: "",
    },
  });

  const watchAmount = paymentForm.watch("amount");
  const watchCashReceived = paymentForm.watch("cashReceived");
  const watchPaymentMethod = paymentForm.watch("payment_method");

  const totalDue = Number(watchAmount) || GRAND_TOTAL;
  const received = Number(watchCashReceived) || 0;

  // Set default payment method to Cash when data loads
  useEffect(() => {
    const currentMethod = paymentForm.getValues("payment_method");
    if (!currentMethod && activePaymentMethods.length > 0) {
      const cashMethod = activePaymentMethods.find((m: any) =>
        m.name.toLowerCase().includes("cash"),
      );
      if (cashMethod) {
        paymentForm.setValue("payment_method", cashMethod.code);
      }
    }
  }, [activePaymentMethods, paymentForm]);

  useEffect(() => {
    // Sync all amount fields with GRAND_TOTAL when it changes
    if (GRAND_TOTAL > 0) {
      paymentForm.setValue("amount", GRAND_TOTAL);
      paymentForm.setValue("onlineAmount", GRAND_TOTAL);
      paymentForm.setValue("chequeAmount", GRAND_TOTAL);
    }
  }, [GRAND_TOTAL, paymentForm]);

  const paymentFields: FormField[] = useMemo(
    () => [
      {
        name: "payment_method",
        label: "Payment Method",
        type: "select",
        options:
          activePaymentMethods.map((m: any) => ({
            label: m.name,
            value: m.code,
          })) || [],
        className: "col-span-12",
      },
      // Cash Fields
      {
        name: "amount",
        label: "Amount",
        type: "number",
        disabled: true,
        showWhen: (values) => values.payment_method?.startsWith("Cash"),
        className: "col-span-4",
      },
      {
        name: "cashReceived",
        label: "Cash Received",
        type: "number",
        placeholder: "Enter amount received",
        showWhen: (values) => values.payment_method?.startsWith("Cash"),
        className: "col-span-4",
      },
      {
        name: "paymentDate",
        label: "Date",
        type: "datepicker",
        showWhen: (values) => values.payment_method?.startsWith("Cash"),
        className: "col-span-4",
      },
      // Online Fields
      {
        name: "onlineAmount",
        label: "Amount",
        type: "number",
        disabled: true,
        showWhen: (values) => values.payment_method?.startsWith("Online"),
        className: "col-span-4",
      },
      {
        name: "referenceNumber",
        label: "Reference Number",
        type: "text",
        placeholder: "Enter reference",
        showWhen: (values) => values.payment_method?.startsWith("Online"),
        className: "col-span-4",
      },
      {
        name: "onlineDate",
        label: "Date",
        type: "datepicker",
        showWhen: (values) => values.payment_method?.startsWith("Online"),
        className: "col-span-4",
      },
      // Cheque Fields
      {
        name: "chequeNo",
        label: "Cheque Number",
        type: "number",
        placeholder: "Enter cheque number",
        showWhen: (values) => values.payment_method?.startsWith("Cheque"),
        className: "col-span-4",
      },
      {
        name: "bankName",
        label: "Bank Name",
        type: "text",
        placeholder: "Enter bank name",
        showWhen: (values) => values.payment_method?.startsWith("Cheque"),
        className: "col-span-4",
      },
      {
        name: "accountHolderName",
        label: "Account Holder's Name",
        type: "text",
        placeholder: "Enter account holder's name",
        showWhen: (values) => values.payment_method?.startsWith("Cheque"),
        className: "col-span-4",
      },
      {
        name: "routingNumber",
        label: "Routing Number",
        type: "number",
        placeholder: "Enter routing number",
        showWhen: (values) => values.payment_method?.startsWith("Cheque"),
        className: "col-span-4",
      },
      {
        name: "bankAccountNumber",
        label: "Bank Account Number",
        type: "number",
        placeholder: "Enter bank account number",
        showWhen: (values) => values.payment_method?.startsWith("Cheque"),
        className: "col-span-4",
      },
      {
        name: "chequeDate",
        label: "Cheque Date",
        type: "datepicker",
        placeholder: "Enter cheque date",
        showWhen: (values) => values.payment_method?.startsWith("Cheque"),
        className: "col-span-4",
      },
      {
        name: "chequeAmount",
        label: "Cheque Amount",
        type: "number",
        placeholder: "Enter cheque amount",
        disabled: true,
        showWhen: (values) => values.payment_method?.startsWith("Cheque"),
        className: "col-span-4",
      },
    ],
    [activePaymentMethods],
  );

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const paymentValues = paymentForm.getValues();
      const selectedPaymentMethod = activePaymentMethods.find(
        (m: any) => m.code === paymentValues.payment_method,
      );

      const isCash = paymentValues.payment_method?.startsWith("Cash");
      const isOnline = paymentValues.payment_method?.startsWith("Online");

      const paymentDateRaw = isCash
        ? paymentValues.paymentDate
        : isOnline
          ? paymentValues.onlineDate
          : paymentValues.chequeDate;
      const paymentDate = paymentDateRaw
        ? format(new Date(paymentDateRaw), "yyyy-MM-dd")
        : format(new Date(), "yyyy-MM-dd");

      let ownershipType = 1;
      if (reviewData?.request?.ownershipDisplay === "Master") ownershipType = 0;
      else if (reviewData?.request?.ownershipDisplay === "Individual")
        ownershipType = 2;
      else if (reviewData?.request?.ownershipDisplay === "Tenant")
        ownershipType = 4;

      const accountStructurePayload: any =
        ownershipType !== 0
          ? {
              ownership_type: ownershipType,
              choice: reviewData?.request?.accountStructurePreference,
            }
          : undefined;

      if (accountStructurePayload) {
        if (
          ownershipType === 1 &&
          reviewData?.request?.accountStructurePreference === "link_different"
        ) {
          accountStructurePayload.new_master_account_no =
            reviewData?.request?.selectedMaster?.accountNo || "";
        } else if (
          ownershipType === 2 &&
          reviewData?.request?.accountStructurePreference === "link_master"
        ) {
          accountStructurePayload.master_account_no =
            reviewData?.request?.selectedMaster?.accountNo || "";
        } else if (
          ownershipType === 4 &&
          reviewData?.request?.accountStructurePreference ===
            "link_different_owner"
        ) {
          accountStructurePayload.selected_owner = {
            account_no: reviewData?.request?.selectedOwner?.accountNo || "",
            name:
              reviewData?.request?.newOwnerName ||
              reviewData?.request?.selectedOwner?.name ||
              "",
            email: reviewData?.request?.newOwnerEmail || "",
            phone: reviewData?.request?.newOwnerPhone || "",
            address:
              reviewData?.request?.newOwnerAddress ||
              reviewData?.request?.selectedOwner?.address ||
              "",
          };
        }
      }

      const additionalData: any = {
        preferred_time_slot: reviewData?.request?.timeSlot || "",
        service_address: {
          type:
            reviewData?.request?.serviceAreaPreference === "new"
              ? "new"
              : "same",
        },
        mailing_address: {
          type:
            reviewData?.request?.mailingAddressPreference === "different"
              ? "different"
              : "same_as_service",
        },
      };

      if (additionalData.service_address.type === "new") {
        additionalData.service_address.house_unit =
          reviewData?.request?.newHouseNumber || "";
        additionalData.service_address.zip =
          reviewData?.request?.newZipCode || "";
        additionalData.service_address.city =
          reviewData?.request?.newCity || "";
        additionalData.service_address.street =
          reviewData?.request?.newStreetAddress || "";
      }

      if (additionalData.mailing_address.type === "different") {
        additionalData.mailing_address.house_unit =
          reviewData?.request?.mailingHouseNumber || "";
        additionalData.mailing_address.zip =
          reviewData?.request?.mailingZipCode || "";
        additionalData.mailing_address.city =
          reviewData?.request?.mailingCity || "";
        additionalData.mailing_address.street =
          reviewData?.request?.mailingStreetAddress || "";
      }

      if (accountStructurePayload) {
        additionalData.account_structure = accountStructurePayload;
      }

      const requestPayload = {
        request_date: reviewData?.request?.scheduledDate,
        consumer_remark: reviewData?.request?.consumerRemark,
        request_type: "Reconnection",
        source: 1, // Default source
        reason: reviewData?.request?.reason,
        priority: reviewData?.request?.priority,
        scheduled_date: {},
        reason_label: reviewData?.request?.reasonLabel,
        time_slot_label: reviewData?.request?.timeSlotLabel,
        time_slot: reviewData?.request?.timeSlot,
        consumer: reviewData?.customer?.id,
        remote_utility_id: remoteUtilityId,
        utility_support_request: reviewData?.request?.utilitySupportRequest,
        service_address: reviewData?.customer?.address,
        contact_phone: reviewData?.customer?.phone,
        contact_email: reviewData?.customer?.email,
        contact_name: reviewData?.customer?.name,
        account_number: reviewData?.customer?.accountNumber,
        consumer_name: reviewData?.customer?.name,
        consumer_email: reviewData?.customer?.email,
        consumer_phone: reviewData?.customer?.phone,
        consumer_address: reviewData?.customer?.address,
        additional_data: additionalData,
      };

      const paymentPayload = {
        amount: GRAND_TOTAL,
        payment_pay_type: selectedPaymentMethod?.id,
        payment_mode: paymentValues.payment_method,
        payment_mode_label:
          selectedPaymentMethod?.name || paymentValues.payment_method,
        payment_date: paymentDate,
        payment_channel: "walk-in",
        status: "CREDIT",
        payment_received_status: 0,
        create_credit_note: false,
        extra_data: {
          bill_amount: GRAND_TOTAL,
          payment_amount: GRAND_TOTAL.toString(),
          excess_refund: 0,
          payment_date: paymentDate,
          ...paymentValues,
        },
      };

      const payload = {
        request: requestPayload,
        payment: paymentPayload,
      };

      // Save final review data
      saveStepData({
        reviewData,
        reviewed: true,
        submittedAt: new Date().toISOString(),
        isStepComplete: true,
      });

      // Clear any errors
      if (clearValidationError) {
        clearValidationError();
      }

      // Create and submit payload
      console.log("Submitting reconnection request payload:", payload);

      createReconnectRequest(payload, {
        onSuccess: () => {
          stepHelpers?.resetAllData();
        },
      });
    } catch (error) {
      console.error("Failed to submit request:", error);
      if (setValidationError) {
        setValidationError("Failed to submit request. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Customer Information */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <User className="h-5 w-5 text-orange-500" />
              Customer Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ValueLabel label="Name" value={reviewData.customer?.name} />
              <ValueLabel
                label="Account Number"
                value={
                  <Badge variant="outline">
                    {reviewData.customer?.accountNumber}
                  </Badge>
                }
              />
              <ValueLabel
                label="Account Type"
                value={
                  <Badge variant="outline" className="uppercase">
                    {reviewData.request?.ownershipDisplay || "Individual"}
                  </Badge>
                }
              />
              <ValueLabel
                label="Category"
                value={
                  <Badge variant="outline" className="uppercase">
                    {reviewData.customer?.type || "INACTIVE"}
                  </Badge>
                }
              />
              <ValueLabel
                label="Email"
                value={
                  <span
                    className="block truncate max-w-[200px]"
                    title={reviewData.customer?.email}
                  >
                    {reviewData.customer?.email}
                  </span>
                }
              />
              <ValueLabel label="Phone" value={reviewData.customer?.phone} />
            </div>
          </CardContent>
        </Card>

        {/* Request Details */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="h-5 w-5 text-green-500" />
              Request Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ValueLabel label="Request Type" value="Reconnection" />
              <ValueLabel
                label="Priority"
                value={
                  <div
                    className={`font-semibold capitalize ${
                      reviewData.request?.priority === "high"
                        ? "text-red-600"
                        : reviewData.request?.priority === "medium"
                          ? "text-orange-600"
                          : "text-blue-600"
                    }`}
                  >
                    {reviewData.request?.priorityLevelDisplay ||
                      reviewData.request?.priority ||
                      "Medium"}
                  </div>
                }
              />
              <ValueLabel
                label="Reason"
                value={reviewData.request?.reasonLabel}
              />
              <ValueLabel
                label="Preferred Date"
                value={reviewData.request?.scheduledDate}
              />
              <ValueLabel
                label="Time Slot"
                value={reviewData.request?.timeSlotLabel}
              />
              {reviewData.request?.consumerRemark && (
                <div className="col-span-1 md:col-span-2">
                  <ValueLabel
                    label="Additional Notes"
                    value={<span>"{reviewData.request.consumerRemark}"</span>}
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Service & Account Configuration */}
        <div className={GRAND_TOTAL > 0 ? "lg:col-span-2" : "lg:col-span-3"}>
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <MapPin className="h-5 w-5 text-purple-500" />
                Service & Account Configuration
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex flex-col h-full space-y-4 col-span-1">
                  <div className="flex items-start gap-3 p-4 border rounded-lg bg-blue-50 h-full">
                    <div className="w-full">
                      <h4 className="font-medium text-sm text-gray-900 mb-4">
                        Service & Mailing
                      </h4>
                      <div className="space-y-4">
                        <ValueLabel
                          label="Service Address"
                          value={reviewData.customer?.address}
                        />
                        <ValueLabel
                          label="Service Area"
                          value={
                            <div>
                              <div>
                                {reviewData.request?.serviceAreaPreference ===
                                "same"
                                  ? "Existing Service Area"
                                  : "New Area Requested"}
                              </div>
                              {reviewData.request?.serviceAreaPreference ===
                                "new" && (
                                <div className="text-xs text-gray-500 mt-1 font-normal leading-relaxed">
                                  {formatAddressDetail(
                                    reviewData.request.newHouseNumber,
                                    reviewData.request.newStreetAddress,
                                    reviewData.request.newCity,
                                    reviewData.request.newZipCode,
                                  )}
                                </div>
                              )}
                            </div>
                          }
                        />
                        <ValueLabel
                          label="Mailing Address"
                          value={
                            <div>
                              <div>
                                {reviewData.request
                                  ?.mailingAddressPreference ===
                                "same_as_service"
                                  ? "Same as Service Address"
                                  : "Different Billing Address"}
                              </div>
                              {reviewData.request?.mailingAddressPreference ===
                                "different" && (
                                <div className="text-xs text-gray-500 mt-1 font-normal leading-relaxed">
                                  {formatAddressDetail(
                                    reviewData.request.mailingHouseNumber,
                                    reviewData.request.mailingStreetAddress,
                                    reviewData.request.mailingCity,
                                    reviewData.request.mailingZipCode,
                                  )}
                                </div>
                              )}
                            </div>
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col h-full space-y-4 col-span-1">
                  <div className="flex items-start gap-3 p-4 border rounded-lg bg-orange-50 h-full">
                    <div className="w-full">
                      <h4 className="font-medium text-sm text-gray-900 mb-4">
                        {reviewData.request?.ownershipDisplay === "Tenant"
                          ? "Owner Linkage"
                          : reviewData.request?.ownershipDisplay ===
                              "Sub-Account"
                            ? "Master Account Linkage"
                            : "Account Structure"}
                      </h4>
                      <div className="space-y-4">
                        <ValueLabel
                          label="Selected Preference"
                          value={
                            <Badge
                              variant="outline"
                              className="uppercase bg-white"
                            >
                              {reviewData.request?.accountStructurePreference?.replace(
                                /_/g,
                                " ",
                              ) || "N/A"}
                            </Badge>
                          }
                        />

                        {/* ── TENANT ── */}
                        {reviewData.request?.ownershipDisplay === "Tenant" && (
                          <>
                            {reviewData.request.accountStructurePreference ===
                            "link_different_owner" ? (
                              <div className="space-y-4 border-t pt-3">
                                <ValueLabel
                                  label="Old Linked Owner"
                                  value={
                                    reviewData.request.currentOwnerInfo
                                      ?.ownerName ||
                                    reviewData.request.currentOwnerInfo?.name ||
                                    "N/A"
                                  }
                                  description={`Acc: ${
                                    reviewData.request.currentOwnerInfo
                                      ?.ownerAccountNo ||
                                    reviewData.request.currentOwnerInfo
                                      ?.accountNo ||
                                    "N/A"
                                  }`}
                                />
                                <ValueLabel
                                  label="New Linked Owner"
                                  value={
                                    reviewData.request.newOwnerName ||
                                    reviewData.request.selectedOwner?.name ||
                                    reviewData.request.selectedOwner
                                      ?.customerName ||
                                    "N/A"
                                  }
                                  description={
                                    reviewData.request.selectedOwner?.accountNo
                                      ? `Acc: ${reviewData.request.selectedOwner.accountNo}`
                                      : undefined
                                  }
                                />
                              </div>
                            ) : reviewData.request
                                .accountStructurePreference === "individual" ? (
                              <p className="text-xs text-neutral-500 mt-2">
                                This account will be detached from the owner
                                upon reconnection.
                              </p>
                            ) : (
                              <ValueLabel
                                label="Linked Owner"
                                value={
                                  reviewData.request.currentOwnerInfo
                                    ?.ownerName ||
                                  reviewData.request.currentOwnerInfo?.name ||
                                  "N/A"
                                }
                                description={`Acc: ${
                                  reviewData.request.currentOwnerInfo
                                    ?.ownerAccountNo ||
                                  reviewData.request.currentOwnerInfo
                                    ?.accountNo ||
                                  "N/A"
                                }`}
                              />
                            )}
                          </>
                        )}

                        {/* ── SUB-ACCOUNT ── */}
                        {reviewData.request?.ownershipDisplay ===
                          "Sub-Account" && (
                          <>
                            {reviewData.request.accountStructurePreference ===
                            "link_different" ? (
                              <div className="space-y-4 border-t pt-3">
                                <ValueLabel
                                  label="Old Master Account"
                                  value={
                                    reviewData.request.currentOwnerInfo
                                      ?.masterName ||
                                    reviewData.request.currentOwnerInfo
                                      ?.ownerName ||
                                    "N/A"
                                  }
                                  description={`Acc: ${
                                    reviewData.request.currentOwnerInfo
                                      ?.masterAccountNo ||
                                    reviewData.request.currentOwnerInfo
                                      ?.ownerAccountNo ||
                                    "N/A"
                                  }`}
                                />
                                <ValueLabel
                                  label="New Master Account"
                                  value={
                                    reviewData.request.selectedMaster?.name ||
                                    reviewData.request.selectedMaster
                                      ?.customerName ||
                                    "N/A"
                                  }
                                  description={`Acc: ${
                                    reviewData.request.selectedMaster
                                      ?.accountNo ||
                                    reviewData.request.selectedMaster
                                      ?.accountNumber ||
                                    "N/A"
                                  }`}
                                />
                              </div>
                            ) : reviewData.request
                                .accountStructurePreference === "individual" ? (
                              <p className="text-xs text-neutral-500 mt-2">
                                This sub-account will be detached and
                                reconnected as a standalone account.
                              </p>
                            ) : (
                              <ValueLabel
                                label="Master Account"
                                value={
                                  reviewData.request.currentOwnerInfo
                                    ?.masterName ||
                                  reviewData.request.currentOwnerInfo
                                    ?.ownerName ||
                                  "N/A"
                                }
                                description={`Acc: ${
                                  reviewData.request.currentOwnerInfo
                                    ?.masterAccountNo ||
                                  reviewData.request.currentOwnerInfo
                                    ?.ownerAccountNo ||
                                  "N/A"
                                }`}
                              />
                            )}
                          </>
                        )}

                        {/* ── INDIVIDUAL / MASTER ── */}
                        {(reviewData.request?.ownershipDisplay ===
                          "Individual" ||
                          reviewData.request?.ownershipDisplay ===
                            "Master") && (
                          <>
                            {reviewData.request.accountStructurePreference ===
                              "link_master" &&
                            reviewData.request.selectedMaster ? (
                              <ValueLabel
                                label="Linked Master Account"
                                value={
                                  reviewData.request.selectedMaster.name ||
                                  reviewData.request.selectedMaster
                                    .customerName ||
                                  "N/A"
                                }
                                description={`Acc: ${
                                  reviewData.request.selectedMaster.accountNo ||
                                  reviewData.request.selectedMaster
                                    .accountNumber ||
                                  "N/A"
                                }`}
                              />
                            ) : (
                              <p className="text-xs text-neutral-500 mt-2">
                                Reconnecting as a standalone individual account.
                              </p>
                            )}
                          </>
                        )}

                        {!reviewData.request?.ownershipDisplay && (
                          <p className="text-xs text-neutral-400">
                            No linked accounts for this request.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Financial Summary */}
        {GRAND_TOTAL > 0 && (
          <div className="lg:col-span-1">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <DollarSign className="h-5 w-5 text-emerald-500" />
                  Financial Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col gap-3">
                  {reviewData.request?.feesBreakdown &&
                  reviewData.request.feesBreakdown.length > 0 ? (
                    reviewData.request.feesBreakdown.map(
                      (fee: any, idx: number) => (
                        <ValueLabel
                          key={idx}
                          label={fee.feeType || fee.fee_type || "Service Fee"}
                          value={`${currency} ${(Number(fee.amount) || 0).toFixed(2)}`}
                        />
                      ),
                    )
                  ) : (
                    <ValueLabel
                      label="Processing Fee"
                      value={`${currency} ${(Number(reviewData.request?.reconnectionFee) || 0).toFixed(2)}`}
                    />
                  )}
                  <div className="pt-3 mt-1 border-t border-neutral-100">
                    <ValueLabel
                      label="Total Payable"
                      value={
                        <span className="text-lg font-bold text-emerald-600">
                          {currency} {GRAND_TOTAL.toFixed(2)}
                        </span>
                      }
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Payment Options Section - Full Width */}
      {GRAND_TOTAL > 0 && (
        <div className="animate-in fade-in slide-in-from-top-4 duration-300">
          <div className="p-6 bg-white border border-neutral-200 rounded-xl shadow-sm">
            <div className="flex items-center gap-3 mb-6 px-1">
              <div className="p-2 bg-blue-50 rounded-lg">
                <PaymentIcon className="h-5 w-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-bold text-neutral-900 tracking-tight">
                Payment Details
              </h3>
            </div>

            <DynamicForm
              form={paymentForm}
              fields={paymentFields}
              gridColumns={12}
              config={{
                spacing: "medium",
                classes: FIELD_CLASSES,
                showSubmitButton: false,
              }}
            />

            <div className="pt-4 border-t border-neutral-100">
              <div className="rounded-xl border border-blue-100 bg-blue-50/30 overflow-hidden divide-y divide-blue-100">
                {/* Total Due / Amount row */}
                <div className="flex justify-between items-center px-4 py-3 text-xs">
                  <span className="text-neutral-500 font-semibold">
                    {watchPaymentMethod?.startsWith("Cash")
                      ? "Total Due:"
                      : "Amount:"}
                  </span>
                  <span className="font-bold text-neutral-800 text-sm">
                    {currency} {GRAND_TOTAL.toFixed(2)}
                  </span>
                </div>

                {watchPaymentMethod?.startsWith("Cash") && (
                  <>
                    {/* Cash Received row */}
                    <div className="flex justify-between items-center px-4 py-3 text-xs">
                      <span className="text-neutral-500 font-semibold">
                        Cash Received:
                      </span>
                      <span className="font-bold text-blue-600 text-sm">
                        {currency} {received.toFixed(2)}
                      </span>
                    </div>

                    {/* Remaining Due — shown when cash received is less than total */}
                    {received > 0 && received < totalDue && (
                      <div className="flex justify-between items-center px-4 py-3 text-xs bg-red-50/50">
                        <span className="text-red-600 font-bold">
                          Remaining Due:
                        </span>
                        <span className="font-bold text-red-600 text-sm">
                          {currency} {(totalDue - received).toFixed(2)}
                        </span>
                      </div>
                    )}

                    {/* Change / Return — shown when cash received exceeds total */}
                    {received > totalDue && (
                      <div className="flex justify-between items-center px-4 py-3 text-xs bg-emerald-50/50">
                        <span className="text-emerald-600 font-bold">
                          Change / Return:
                        </span>
                        <span className="font-bold text-emerald-600 text-sm">
                          {currency} {(received - totalDue).toFixed(2)}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ready to Submit Banner */}
      <div className="p-4 bg-blue-50/30 border border-blue-100 rounded-xl shadow-sm">
        <div className="flex gap-3">
          <ShieldCheck className="h-6 w-6 text-blue-600 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-bold text-blue-900">
              Final Review Complete
            </h4>
            <p className="text-xs text-blue-700/80 mt-1 font-medium leading-relaxed">
              All details have been verified. Click "Submit Request" to process
              the reconnection and payment for this account.
            </p>
          </div>
        </div>
      </div>

      {/* Footer Navigation */}
      <div className="flex items-center justify-between pt-8 border-t border-neutral-200 mt-8">
        <Button onClick={onPrevious} variant="outline" disabled={isSubmitting}>
          <ChevronLeft size={16} />
          <span>Previous Step</span>
        </Button>

        <Button onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? (
            <Loader2 className="animate-spin h-5 w-5 text-white" />
          ) : (
            <Send size={18} />
          )}
          <span>{isSubmitting ? "Processing..." : "Pay & Submit Request"}</span>
        </Button>
      </div>
    </div>
  );
}
